import React, { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { Container, Box, Typography, TextField, Button, Link, Paper, Alert, CircularProgress, InputAdornment, IconButton, Grid, FormControl, InputLabel, Select, MenuItem, FormControlLabel, Checkbox, Divider } from '@mui/material';
import { Visibility, VisibilityOff, Email, Lock, Person, Phone, Checkroom, AutoAwesome, Google } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { faker } from '@faker-js/faker';
import getAppConfig from '../../config/app-config';
import getConfig from '../../config/app-config';

const Register = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    surname: '',
    telephone: '',
    telephoneSecondary: '',
    shirtSize: '',
    level: '',
    flagStatuto: false,
    flagPrivacy: false,
    flagPicture: false,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});

  const { register, loading, error, clearError } = useAuth();
  const navigate = useNavigate();
  const config = getAppConfig();
  const appConfig = getConfig();

  const shirtSizes = ['XS', 'S', 'M', 'L', 'XL', 'XXL'];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));

    // Clear field error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }

    // Clear global error when user starts typing
    if (error) {
      clearError();
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email) {
      newErrors.email = 'Email è obbligatoria';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email non valida';
    }

    if (!formData.password) {
      newErrors.password = 'Password è obbligatoria';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password deve essere di almeno 6 caratteri';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Conferma password è obbligatoria';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Le password non coincidono';
    }

    if (!formData.name) {
      newErrors.name = 'Nome è obbligatorio';
    } else if (formData.name.length > 50) {
      newErrors.name = 'Nome non può superare 50 caratteri';
    }

    if (!formData.surname) {
      newErrors.surname = 'Cognome è obbligatorio';
    } else if (formData.surname.length > 50) {
      newErrors.surname = 'Cognome non può superare 50 caratteri';
    }

    if (!formData.telephone) {
      newErrors.telephone = 'Telefono è obbligatorio';
    } else if (formData.telephone.length > 20) {
      newErrors.telephone = 'Telefono non può superare 20 caratteri';
    }

    if (formData.telephoneSecondary && formData.telephoneSecondary.length > 20) {
      newErrors.telephoneSecondary = 'Telefono secondario non può superare 20 caratteri';
    }

    if (!formData.shirtSize) {
      newErrors.shirtSize = 'Taglia maglietta è obbligatoria';
    }

    if (!formData.level) {
      newErrors.level = 'Anno di corso è obbligatorio';
    }

    if (!formData.flagStatuto) {
      newErrors.flagStatuto = 'Devi accettare lo statuto';
    }

    if (!formData.flagPrivacy) {
      newErrors.flagPrivacy = 'Devi accettare la privacy policy';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Remove confirmPassword from data sent to API and convert level to number
    const { confirmPassword, level, ...registrationData } = formData;
    if (level === 'Da assegnare') {
      registrationData.level = 0;
    } else if (level === 'Oltre il 5° anno') {
      registrationData.level = 6; // Assegna 6 per "Oltre il 5° anno"
    } else {
      registrationData.level = parseInt(level.split('°')[0]); // Convert "1° anno" to 1
    }

    const result = await register(registrationData);
    if (result.success) {
      navigate('/registration-success');
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleToggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleGoogleLogin = () => {
    // Reindirizza al server per l'autenticazione Google
    window.location.href = `${config.SERVER_URL}/api/auth/google`;
  };

  const handleAutoFill = () => {
    const randomData = {
      email: 'giggioz@gmail.com',
      password: 'xxxxxx',
      confirmPassword: 'xxxxxx',
      name: faker.person.firstName(),
      surname: faker.person.lastName(),
      telephone: '338 42 91 451',
      telephoneSecondary: '',
      shirtSize: faker.helpers.arrayElement(['XS', 'S', 'M', 'L', 'XL', 'XXL']),
      level: faker.helpers.arrayElement(['Da assegnare', '1° anno', '2° anno', '3° anno', '4° anno', '5° anno', 'Oltre il 5° anno']),
      flagStatuto: true,
      flagPrivacy: true,
      flagPicture: false,
    };

    setFormData(randomData);
    setErrors({});
    clearError();
  };

  return (
    <Container component='main' maxWidth={false} disableGutters>
      <Box
        sx={{
          marginTop: 0,
          marginBottom: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          minHeight: '100vh',
          padding: { xs: 2, sm: 4 },
        }}
      >
        <Paper
          elevation={3}
          sx={{
            padding: { xs: 3, sm: 4 },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            maxWidth: { sm: 'md' },
            margin: 'auto',
          }}
        >
          <Typography component='h1' variant='h4' gutterBottom>
            Registrazione
          </Typography>

          <Typography variant='body2' color='text.secondary' align='center' sx={{ mb: 3 }}>
            Crea il tuo account per accedere ai servizi dell'associazione
          </Typography>

          {process.env.NODE_ENV === 'development' && (
            <Button variant='outlined' startIcon={<AutoAwesome />} onClick={handleAutoFill} sx={{ mb: 2 }} color='secondary'>
              Auto Fill (Dev)
            </Button>
          )}

          {/* Google Registration Button - Only show if USE_GOOGLE is enabled */}
          {appConfig.USE_GOOGLE && (
            <>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<Google />}
                onClick={handleGoogleLogin}
                sx={{ 
                  mb: 2, 
                  py: 1.5,
                  borderColor: '#db4437',
                  color: '#db4437',
                  '&:hover': {
                    borderColor: '#c23321',
                    backgroundColor: 'rgba(219, 68, 55, 0.04)'
                  }
                }}
              >
                Registrati con Google
              </Button>

              <Divider sx={{ width: '100%', my: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  oppure
                </Typography>
              </Divider>
            </>
          )}

          {error && (
            <Alert severity='error' sx={{ width: '100%', mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component='form' onSubmit={handleSubmit} sx={{ width: '100%' }}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  required
                  fullWidth
                  id='name'
                  label='Nome'
                  name='name'
                  autoComplete='given-name'
                  value={formData.name}
                  onChange={handleChange}
                  error={!!errors.name}
                  helperText={errors.name}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position='start'>
                        <Person color='action' />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField required fullWidth id='surname' label='Cognome' name='surname' autoComplete='family-name' value={formData.surname} onChange={handleChange} error={!!errors.surname} helperText={errors.surname} />
              </Grid>
              <Grid size={12}>
                <TextField
                  required
                  fullWidth
                  id='email'
                  label='Email'
                  name='email'
                  autoComplete='email'
                  value={formData.email}
                  onChange={handleChange}
                  error={!!errors.email}
                  helperText={errors.email}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position='start'>
                        <Email color='action' />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  required
                  fullWidth
                  id='telephone'
                  label='Telefono'
                  name='telephone'
                  autoComplete='tel'
                  value={formData.telephone}
                  onChange={handleChange}
                  error={!!errors.telephone}
                  helperText={errors.telephone}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position='start'>
                        <Phone color='action' />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  id='telephoneSecondary'
                  label='Telefono Secondario'
                  name='telephoneSecondary'
                  autoComplete='tel'
                  value={formData.telephoneSecondary}
                  onChange={handleChange}
                  error={!!errors.telephoneSecondary}
                  helperText={errors.telephoneSecondary}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position='start'>
                        <Phone color='action' />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth required error={!!errors.shirtSize}>
                  <InputLabel id='shirt-size-label'>Taglia Maglietta</InputLabel>
                  <Select
                    labelId='shirt-size-label'
                    id='shirtSize'
                    name='shirtSize'
                    value={formData.shirtSize}
                    label='Taglia Maglietta'
                    onChange={handleChange}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position='start'>
                          <Checkroom color='action' />
                        </InputAdornment>
                      ),
                    }}
                  >
                    {shirtSizes.map((size) => (
                      <MenuItem key={size} value={size}>
                        {size}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.shirtSize && (
                    <Typography variant='caption' color='error' display='block' sx={{ mt: 0.5 }}>
                      {errors.shirtSize}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth required error={!!errors.level}>
                  <InputLabel id='level-label'>Anno di corso</InputLabel>
                  <Select
                    labelId='level-label'
                    id='level'
                    name='level'
                    value={formData.level}
                    label='Anno di corso'
                    onChange={handleChange}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position='start'>
                          <Checkroom color='action' />
                        </InputAdornment>
                      ),
                    }}
                  >
                    <MenuItem value='Da assegnare'>Da assegnare</MenuItem>
                    <MenuItem value='1° anno'>1° anno</MenuItem>
                    <MenuItem value='2° anno'>2° anno</MenuItem>
                    <MenuItem value='3° anno'>3° anno</MenuItem>
                      <MenuItem value='4° anno'>4° anno</MenuItem>
                      <MenuItem value='5° anno'>5° anno</MenuItem>
                      <MenuItem value='Oltre il 5° anno'>Oltre il 5° anno</MenuItem>
                  </Select>
                  {errors.level && (
                    <Typography variant='caption' color='error' display='block' sx={{ mt: 0.5 }}>
                      {errors.level}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              <Grid size={12}>
                <TextField
                  required
                  fullWidth
                  name='password'
                  label='Password'
                  type={showPassword ? 'text' : 'password'}
                  id='password'
                  autoComplete='new-password'
                  value={formData.password}
                  onChange={handleChange}
                  error={!!errors.password}
                  helperText={errors.password}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position='start'>
                        <Lock color='action' />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position='end'>
                        <IconButton aria-label='toggle password visibility' onClick={handleTogglePasswordVisibility} edge='end'>
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  required
                  fullWidth
                  name='confirmPassword'
                  label='Conferma Password'
                  type={showConfirmPassword ? 'text' : 'password'}
                  id='confirmPassword'
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  error={!!errors.confirmPassword}
                  helperText={errors.confirmPassword}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position='start'>
                        <Lock color='action' />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position='end'>
                        <IconButton aria-label='toggle confirm password visibility' onClick={handleToggleConfirmPasswordVisibility} edge='end'>
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid size={12}>
                <Box sx={{ mb: 2, p: 2, bgcolor: 'primary.light', borderRadius: 1, border: '2px solid', borderColor: 'primary.main' }}>
                  <Link
                    href={process.env.REACT_APP_ASSOCIATE ? `/docs/${process.env.REACT_APP_ASSOCIATE}/regolamento.pdf` : `/docs/default-regolamento.pdf`}
                    target='_blank'
                    rel='noopener noreferrer'
                    variant='body1'
                    color='primary.contrastText'
                    sx={{
                      textDecoration: 'none',
                      fontWeight: 'bold',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      '&:hover': {
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    📄 Visualizza lo statuto dell'associazione
                  </Link>
                </Box>
                <FormControlLabel control={<Checkbox name='flagStatuto' checked={formData.flagStatuto} onChange={handleChange} color='primary' required />} label="Accetto lo statuto dell'associazione" />
                {errors.flagStatuto && (
                  <Typography variant='caption' color='error' display='block'>
                    {errors.flagStatuto}
                  </Typography>
                )}
              </Grid>
              <Grid size={12}>
                <FormControlLabel control={<Checkbox name='flagPrivacy' checked={formData.flagPrivacy} onChange={handleChange} color='primary' required />} label='Accetto la privacy policy' />
                {errors.flagPrivacy && (
                  <Typography variant='caption' color='error' display='block'>
                    {errors.flagPrivacy}
                  </Typography>
                )}
              </Grid>
              <Grid size={12}>
                <FormControlLabel control={<Checkbox name='flagPicture' checked={formData.flagPicture} onChange={handleChange} color='primary' />} label='Accetto liberatoria foto' />
              </Grid>
            </Grid>

            <Button type='submit' fullWidth variant='contained' sx={{ mt: 3, mb: 2, py: 1.5 }} disabled={loading}>
              {loading ? <CircularProgress size={24} color='inherit' /> : 'Registrati'}
            </Button>

            <Box sx={{ textAlign: 'center' }}>
              <Link component={RouterLink} to='/login' variant='body2'>
                Hai già un account? Accedi
              </Link>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default Register;
